import { useEffect, useState } from 'react'
import api from '@/lib/api'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { Plus, Copy, Check, Ban, CalendarPlus } from 'lucide-react'

interface AccessCodeRow {
  id: string
  code: string
  label: string
  contact_email: string | null
  partner_email: string | null
  tier: string
  duration_days: number
  max_calls: number | null
  max_chats: number | null
  created_at: string
  used_at: string | null
  revoked_at: string | null
  revoked_reason: string | null
  used_by_org_id: string | null
  org_name: string | null
  org_slug: string | null
  demo_expires_at: string | null
  demo_archived_at: string | null
  org_status: string | null
  created_by_email: string | null
  status: 'unredeemed' | 'active' | 'expired' | 'archived' | 'revoked'
}

const STATUS_BADGE: Record<AccessCodeRow['status'], { label: string; className: string }> = {
  unredeemed: { label: 'Unredeemed', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  active:     { label: 'Active',     className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  expired:    { label: 'Expired',    className: 'bg-amber-100 text-amber-700 border-amber-200' },
  archived:   { label: 'Archived',   className: 'bg-zinc-200 text-zinc-700 border-zinc-300' },
  revoked:    { label: 'Revoked',    className: 'bg-rose-100 text-rose-700 border-rose-200' },
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}

export default function AccessCodesPage() {
  const { toast } = useToast()
  const brand = useBrand()
  const [codes, setCodes] = useState<AccessCodeRow[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [extendTarget, setExtendTarget] = useState<AccessCodeRow | null>(null)
  const [extendDays, setExtendDays] = useState(30)
  const [revokeTarget, setRevokeTarget] = useState<AccessCodeRow | null>(null)
  const [revokeReason, setRevokeReason] = useState('')

  const [form, setForm] = useState({
    label: '',
    contactEmail: '',
    partnerEmail: '',
    tier: 'partner' as 'associate' | 'partner' | 'principal',
    durationDays: 30,
    maxCalls: '' as string,
    maxChats: '' as string,
  })

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/access-codes')
      setCodes(res.data?.data || [])
    } catch (err: any) {
      toast({
        title: 'Failed to load access codes',
        description: err?.response?.data?.message || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  async function handleCreate() {
    if (!form.label.trim()) {
      toast({ title: 'Recipient label required', variant: 'destructive' })
      return
    }
    setSubmitting(true)
    try {
      await api.post('/api/access-codes', {
        label: form.label.trim(),
        contactEmail: form.contactEmail.trim() || undefined,
        partnerEmail: form.partnerEmail.trim() || undefined,
        tier: form.tier,
        durationDays: form.durationDays,
        maxCalls: form.maxCalls ? Number(form.maxCalls) : undefined,
        maxChats: form.maxChats ? Number(form.maxChats) : undefined,
      })
      toast({ title: 'Code created', description: `${form.label} now has a code.` })
      setCreateOpen(false)
      setForm({ label: '', contactEmail: '', partnerEmail: '', tier: 'partner', durationDays: 30, maxCalls: '', maxChats: '' })
      await load()
    } catch (err: any) {
      toast({
        title: 'Failed to create code',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRevoke() {
    if (!revokeTarget) return
    try {
      await api.post(`/api/access-codes/${revokeTarget.id}/revoke`, {
        reason: revokeReason.trim() || undefined,
      })
      toast({ title: 'Code revoked' })
      setRevokeTarget(null)
      setRevokeReason('')
      await load()
    } catch (err: any) {
      toast({
        title: 'Failed to revoke',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }

  async function handleExtend() {
    if (!extendTarget) return
    try {
      await api.post(`/api/access-codes/${extendTarget.id}/extend`, {
        additionalDays: extendDays,
      })
      toast({ title: 'Demo extended', description: `+${extendDays} days for ${extendTarget.label}.` })
      setExtendTarget(null)
      setExtendDays(30)
      await load()
    } catch (err: any) {
      toast({
        title: 'Failed to extend',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }

  function copyRedemptionUrl(code: string) {
    const url = `${window.location.origin}/redeem?code=${encodeURIComponent(code)}`
    navigator.clipboard.writeText(url).then(() => {
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 2000)
      toast({ title: 'Link copied', description: 'Paste it into an email or DM.' })
    })
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="The Castle"
        title="Access Codes"
        subtitle="Single-use redemption codes for demo access — no card required, 30-day default. Each code is labeled per recipient so you can track who's actually engaging."
      />

      <Card className="card-inset">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">All codes</CardTitle>
              <CardDescription>
                {codes.length} total · {codes.filter(c => c.status === 'active').length} active · {codes.filter(c => c.status === 'unredeemed').length} unredeemed
              </CardDescription>
            </div>
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1.5" />
              New code
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : codes.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No codes yet. Click "New code" to generate one.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Redeemed by</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map((row) => {
                  const badge = STATUS_BADGE[row.status]
                  const days = daysUntil(row.demo_expires_at)
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-2">
                          <span>{row.code}</span>
                          <button
                            onClick={() => copyRedemptionUrl(row.code)}
                            className="text-muted-foreground hover:text-foreground"
                            title="Copy redemption link"
                          >
                            {copiedCode === row.code ? (
                              <Check className="h-3.5 w-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium flex items-center gap-1.5">
                          {row.label}
                          {row.partner_email && (
                            <Badge variant="outline" className="bg-violet-100 text-violet-700 border-violet-200 text-[10px] px-1.5 py-0">
                              Partner
                            </Badge>
                          )}
                        </div>
                        {row.contact_email && (
                          <div className="text-xs text-muted-foreground">{row.contact_email}</div>
                        )}
                        {row.partner_email && (
                          <div className="text-xs text-violet-700/80">Partner: {row.partner_email}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={badge.className}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm capitalize">{row.tier}</TableCell>
                      <TableCell>
                        {row.org_name ? (
                          <a
                            href={`/admin/organizations/${row.used_by_org_id}`}
                            className="text-sm text-brand-royal hover:underline"
                          >
                            {row.org_name}
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.demo_expires_at ? (
                          <span className={days !== null && days < 7 ? 'text-amber-600' : ''}>
                            {days !== null && days >= 0
                              ? `${days} day${days === 1 ? '' : 's'} left`
                              : 'expired'}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {row.status === 'active' || row.status === 'expired' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setExtendTarget(row); setExtendDays(30) }}
                              title="Extend demo"
                            >
                              <CalendarPlus className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                          {row.status !== 'revoked' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRevokeTarget(row)}
                              title="Revoke"
                              className="text-rose-600 hover:text-rose-700"
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New access code</DialogTitle>
            <DialogDescription>
              Single-use code, no card required for the recipient. Default duration is 30 days, after which the demo locks read-only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="label">Recipient label</Label>
              <Input
                id="label"
                placeholder="e.g. Sarah Lopez at Mendable Marketing"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
              <p className="text-[11px] text-muted-foreground">
                The first word becomes part of the code (e.g. <code>LL-SARAH-X4F2</code>).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Contact email (optional)</Label>
              <Input
                id="email"
                type="email"
                placeholder="sarah@mendable.com"
                value={form.contactEmail}
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="partner-email">Agency partner email (optional)</Label>
              <Input
                id="partner-email"
                type="email"
                placeholder="brandon@marketingsix.com"
                value={form.partnerEmail}
                onChange={(e) => setForm({ ...form, partnerEmail: e.target.value })}
              />
              <p className="text-[11px] text-muted-foreground">
                Set this when the recipient already has a {splitBrandName(brand.appName)[0]} account and is setting up a firm on someone else's behalf. They'll be added as admin (relationship: agency partner) instead of being asked to sign up again.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tier</Label>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v as any })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="associate">Associate</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="principal">Principal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (days)</Label>
                <Input
                  id="duration"
                  type="number"
                  min={1}
                  max={365}
                  value={form.durationDays}
                  onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value) || 30 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="maxCalls">Max calls (optional)</Label>
                <Input
                  id="maxCalls"
                  type="number"
                  min={1}
                  placeholder="unlimited"
                  value={form.maxCalls}
                  onChange={(e) => setForm({ ...form, maxCalls: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxChats">Max chats (optional)</Label>
                <Input
                  id="maxChats"
                  type="number"
                  min={1}
                  placeholder="unlimited"
                  value={form.maxChats}
                  onChange={(e) => setForm({ ...form, maxChats: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={submitting}>
              {submitting ? 'Creating…' : 'Generate code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend dialog */}
      <Dialog open={extendTarget !== null} onOpenChange={(open) => !open && setExtendTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Extend demo</DialogTitle>
            <DialogDescription>
              {extendTarget?.label}'s demo expires{' '}
              {extendTarget?.demo_expires_at
                ? new Date(extendTarget.demo_expires_at).toLocaleDateString()
                : '—'}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="extend-days">Add days</Label>
            <Input
              id="extend-days"
              type="number"
              min={1}
              max={365}
              value={extendDays}
              onChange={(e) => setExtendDays(Number(e.target.value) || 30)}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExtendTarget(null)}>Cancel</Button>
            <Button onClick={handleExtend}>Extend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={revokeTarget !== null} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Revoke code?</DialogTitle>
            <DialogDescription>
              {revokeTarget?.used_at
                ? `This will lock ${revokeTarget?.org_name || 'the redeeming org'} to demo_expired.`
                : 'This code will become unusable. If unredeemed, no one can claim it.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="revoke-reason">Reason (optional)</Label>
            <Input
              id="revoke-reason"
              placeholder="e.g. shared without permission"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRevoke}>Revoke</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
